import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getLabTests, TestCategory } from "@/services/data";
import TestCard from "@/components/TestCard";
import { Skeleton } from "@/components/ui/skeleton";

const categories: TestCategory[] = [
  "Blood Test", "Urine Test", "Thyroid", "Diabetes", "Heart Health",
  "Liver", "Kidney", "Vitamins", "Allergy", "Cancer Marker",
];

export default function TestsPage() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [activeCategory, setActiveCategory] = useState<TestCategory | null>(
    (searchParams.get("category") as TestCategory) || null
  );
  const [fastingFilter, setFastingFilter] = useState<boolean | null>(null);
  const [homeFilter, setHomeFilter] = useState<boolean | null>(null);

  const { data: tests, isLoading } = useQuery({ queryKey: ["tests"], queryFn: getLabTests });

  const filtered = useMemo(() => {
    if (!tests) return [];
    return tests.filter(t => {
      const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.category.toLowerCase().includes(search.toLowerCase());
      const matchCategory = !activeCategory || t.category === activeCategory;
      const matchFasting = fastingFilter === null || t.fasting === fastingFilter;
      const matchHome = homeFilter === null || t.homeCollection === homeFilter;
      return matchSearch && matchCategory && matchFasting && matchHome;
    });
  }, [tests, search, activeCategory, fastingFilter, homeFilter]);

  const clearFilters = () => {
    setSearch("");
    setActiveCategory(null);
    setFastingFilter(null);
    setHomeFilter(null);
  };

  const hasFilters = search || activeCategory || fastingFilter !== null || homeFilter !== null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Lab Tests</h1>
        <p className="text-muted-foreground">Book from 500+ tests at affordable prices with home sample collection</p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search tests by name or category..."
          className="pl-9 h-11"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Category filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setActiveCategory(null)}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${!activeCategory ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${activeCategory === cat ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Secondary filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
        <button
          onClick={() => setHomeFilter(homeFilter === true ? null : true)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${homeFilter === true ? "bg-green-600 text-white border-green-600" : "border-border text-muted-foreground hover:border-green-500"}`}
        >
          🏠 Home Collection
        </button>
        <button
          onClick={() => setFastingFilter(fastingFilter === false ? null : false)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${fastingFilter === false ? "bg-blue-600 text-white border-blue-600" : "border-border text-muted-foreground hover:border-blue-500"}`}
        >
          No Fasting Required
        </button>
        {hasFilters && (
          <button onClick={clearFilters} className="px-3 py-1 rounded-full text-xs text-destructive border border-destructive/30 hover:bg-destructive/10 transition-colors flex items-center gap-1">
            <X className="h-3 w-3" /> Clear Filters
          </button>
        )}
        <span className="ml-auto text-sm text-muted-foreground">{filtered.length} tests found</span>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border p-4 space-y-3">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-8 w-full mt-4" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Search className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium mb-2">No tests found</p>
          <p className="text-sm">Try adjusting your search or filters</p>
          <Button variant="outline" className="mt-4" onClick={clearFilters}>Clear All Filters</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(test => <TestCard key={test.id} test={test} />)}
        </div>
      )}
    </div>
  );
}
